import {groupBy, groupByUniqueKey, mapObject, objectFromEntries, range} from '@benchling/js-utils';
import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert, assertAndContinue} from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import {Annotation, SequencePrimer} from 'BioJS';
import {SequencePrime} from 'biojs/BioConstants';
import {Sequence as BioJSSequence, SequenceAttributes} from 'biojs/Sequence';
import {Translation} from 'biojs/Translation';
import {Sequence} from 'components/Sequence';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {EventConstants} from 'constants/EventConstants';
import {PolymerSubtype, Side} from 'constants/MolbioConstants';
import {SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP, SequenceTopology} from 'constants/SequenceConstants';
import {DisplaySequenceFileState} from 'FileStates';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import AppDispatcher from 'lib/Dispatcher';
import {AssemblyFrontendFragmentValidator} from 'molbio/bulk-assembly/AssemblyFrontendFragmentValidator';
import {AssemblyLayoutValidator} from 'molbio/bulk-assembly/AssemblyLayoutValidator';
import {AssemblySelector} from 'molbio/bulk-assembly/AssemblySelector';
import {BulkAssemblyActionType} from 'molbio/bulk-assembly/BulkAssemblyActions';
import {
  BasicPolymerInfo,
  BULK_ASSEMBLY_METHOD_TO_FRAGMENT_PRODUCTION_METHOD_TO_DISPLAY_PARAMS,
  BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY,
  BulkAssemblyBin,
  BulkAssemblyBinType,
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  BulkAssemblySpacerBin,
  ConstructFragment,
  ConstructUpdate,
  DisplayBin,
  TableConstruct,
  TableConstructFragment,
  TableFragment,
  DisplayFragmentProductionMethod,
  DisplayOnlyFragmentProductionMethod,
  FragmentProductionMethod,
  FragmentUpdate,
  isBulkAssemblyFragmentBin,
  isBulkAssemblySpacerBin,
  isRestrictionEnzymeBulkAssemblyType,
  isSpacerSupportingBulkAssemblyType,
  METHOD_TO_DISPLAY,
  METHOD_TO_SPACER_NAME,
  MethodParameters,
  NormalizedConstruct,
  NormalizedFragment,
  OPPOSITE_BA_ORIENTATION,
  PreviewConstruct,
  ProteinPolymerInfo,
  RestrictionEnzymeTableInfo,
  SequencePolymerInfo,
  SKIP_BIN_SENTINEL,
  SpacerSupportingBulkAssemblyType,
  SpecifiedFragmentInfo,
  BulkAssemblyOligoSource,
  BulkAssemblyTopology,
  getMaxConstructCount,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import BulkAssemblyPolymerData from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {constructPolymerIsSequence} from 'molbio/bulk-assembly/BulkAssemblyValidation';
import {
  BaseDisplayPrimer,
  BulkAssemblyLayoutError,
  BulkAssemblyValidationMessage,
  BulkAssemblyValidationMessages,
  DisplayPrimer,
  getErrorText,
  PrimerType,
  ProcessedAsyncBulkAssemblyValidationResults,
  BulkAssemblyConstructValidationResult,
  AssembledSequenceConstruct,
  AssembledProteinConstruct,
  ValidationConstructFragment,
  ValidationStatus,
} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {
  BA_EMPTY_VALUE,
  baId,
  BAIdValue,
  baOrientation,
  BARawTextValue,
  BAValueType,
  BULK_ASSEMBLY_ORIENTATION_NAME,
  BulkAssemblyOrientation,
  formatBAEndpoint,
  getOrientationWithFallback,
  isBAId,
  isBANumber,
  isBAOrientation,
  isBARawText,
  isEmptyBAValue,
  parseBAEndpoint,
  parseBAOrientation,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import BulkAssemblyStateManager, {
  DestructiveActionsForApproval,
} from 'molbio/bulk-assembly/state/BulkAssemblyStateManager';
import {BULK_ASSEMBLY_LOADING_MESSAGE} from 'molbio/bulk-assembly/tables/BulkAssemblyTableUtil';
import {
  cartesianProduct,
  constructFragmentData,
  generateNewBinName,
  getNewBinColor,
  isProteinBasedAssembly,
  methodCanSpecifyEndpoints,
  polymerDataFromFragment,
} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {
  getPreferredRestrictionEnzymeId,
  getRestrictionEnzymeInfo,
  areEnzymesRequired,
} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {
  getAutopopulatedFragmentDetails,
  getDisplayToModelConverters,
  getModelToDisplayConverters,
  getValueForOrientation,
  handleEndpointChange,
  validateEndpoints,
} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import {getNameResolver, NameResolutionType, NameResolver} from 'molbio/bulk-assembly/util/NameResolution';
import {VisualPolymer} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import {getCuts, getEnzyme} from 'molbio/enzymes/EnzymeUtil';
import {TEMP_SEQUENCE_ID} from 'molbio/MolBioConstants';
import Protein from 'molbio/proteins/Protein';
import * as sequtil from 'molbio/SeqUtil';
import {modelEndToDisplay, modelStartToDisplay} from 'molbio/SeqUtil';
import {getStore} from 'redux-stores/Store';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {capitalize} from 'util/NameUtil';
import sortedBy from 'util/sortedBy';
import {LOADING} from 'util/Types';

function getEmptyNormalizedFragment(): NormalizedFragment {
  return {
    cid: uuidv4(),
    sequence: BA_EMPTY_VALUE,
    sequenceVersionDbId: null,
    protein: BA_EMPTY_VALUE,
    proteinVersionDbId: null,
    bin: BA_EMPTY_VALUE,
    start: BA_EMPTY_VALUE,
    end: BA_EMPTY_VALUE,
    orientation: baOrientation(BulkAssemblyOrientation.FORWARD),
    preferredPrimer5: BA_EMPTY_VALUE,
    preferredPrimer3: BA_EMPTY_VALUE,
    restrictionEnzyme5: BA_EMPTY_VALUE,
    restrictionEnzyme3: BA_EMPTY_VALUE,
  };
}

/**
 * Core class that coordinates all business logic for presenting and updating bulk assemblies.
 *
 * Many display and validation details in bulk assembly require aggregating and cross-referencing
 * different parts of the assembly state, so it generally wouldn't be appropriate for individual
 * components to own this logic. Both display and update logic need to cross-reference with various
 * pieces of information fetched from GraphQL queries, so a Redux-driven approach would also be too
 * limiting. Instead, we centralize logic in this class with all dependencies as fields, and
 * decompose it as necessary.
 *
 * The useAssemblyCoordinator hook is responsible for loading and constructing all relevant
 * dependencies.
 */
export class AssemblyCoordinator {
  fragmentsByCid: {[id: string]: NormalizedFragment};
  assemblyLayoutValidator: AssemblyLayoutValidator;
  assemblySelector: AssemblySelector;
  topology: SequenceTopology;
  duplicateFragmentNamesByBin: {[binCid: string]: Set<string>};
  specifiedFragmentInfosByBinCid: {[binCid: string]: Array<SpecifiedFragmentInfo>};

  constructor(
    readonly stateManager: BulkAssemblyStateManager,
    readonly assemblyId: string,
    readonly isFinalized: boolean,
    readonly isArchived: boolean,
    readonly permissions: Permissions,
    readonly state: BulkAssemblyProcedure,
    readonly name: string,
    readonly folderId: string,
    readonly polymerData: BulkAssemblyPolymerData,
    readonly validationResults: ProcessedAsyncBulkAssemblyValidationResults,
    readonly refetchAssembly: () => Promise<unknown>,
    readonly refetchValidation: () => Promise<unknown>,
    readonly fragmentStatusFilter: ValidationStatus | null,
    readonly constructStatusFilter: ValidationStatus | null
  ) {
    this.fragmentsByCid = groupByUniqueKey(this.state.fragments, (fragment) => fragment.cid);
    this.assemblyLayoutValidator = new AssemblyLayoutValidator(this.state);
    this.assemblySelector = new AssemblySelector(this.state);
    this.topology = BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY[this.state.topology];
    this.duplicateFragmentNamesByBin = this.getDuplicateFragmentNamesByBin();
    this.specifiedFragmentInfosByBinCid = this.getSpecifiedFragmentInfosByBinCid();
  }

  async doRefetchAssembly(): Promise<void> {
    await this.refetchAssembly();
  }

  async doRefetchValidation(): Promise<void> {
    await this.refetchValidation();
  }

  /**
   * This method is called outside of render in a useEffect (without a dependency list), and can be
   * used for things like autofilling state in response to data being loaded.
   *
   * IMPORTANT: BECAUSE THIS METHOD IS CALLED AFTER EVERY RENDER, IT MUST BE WRITTEN CAREFULLY TO
   * AVOID INFINITE RENDER LOOPS. ALL STATE CHANGES PERFORMED BY THIS FUNCTION MUST "CONVERGE" IN
   * THE SENSE THAT LATER CALLS TO THIS FUNCTION DON'T PERFORM ANY STATE CHANGE BECAUSE THERE IS NO
   * MORE WORK TO DO.
   */
  performEffectsAfterRender(): void {
    let updatedFragments = false;
    const fragments: Array<NormalizedFragment> = [];
    const method = this.assemblySelector.getMethod();
    for (const fragment of this.state.fragments) {
      const constructFragmentIndex = this.assemblySelector.getConstructFragmentIndex(fragment);
      const autopopulatedFragmentDetails = getAutopopulatedFragmentDetails(
        fragment,
        this.polymerData,
        this.canSpecifyEndpoints(),
        getPreferredRestrictionEnzymeId(method),
        constructFragmentIndex,
        this.assemblySelector.getNumFragmentBins(),
        this.topology,
        this.assemblySelector.getFragmentProductionMethodForBin(fragment.bin)
      );
      // Track if any fragment gets updated.
      updatedFragments ||= autopopulatedFragmentDetails != null;
      fragments.push({...fragment, ...autopopulatedFragmentDetails});
    }
    if (updatedFragments) {
      this.resolveAndUpdateState({...this.state, fragments});
    }
  }

  /**
   * Replace the redux state with this state, performing automatic cleanups like disconnecting
   * invalid IDs.
   */
  async resolveAndUpdateState(
    newState: BulkAssemblyProcedure,
    approveDestructiveActions?: (
      destructiveActionsForApproval: DestructiveActionsForApproval
    ) => Promise<boolean>
  ): Promise<boolean> {
    const {resolvedState, destructiveActions} = this.stateManager.resolveState(newState);
    let shouldUpdateState = true;
    if (Object.keys(destructiveActions).length > 0) {
      assertAndContinue(
        approveDestructiveActions != null,
        'Unexpectedly received new state with destructive side effects'
      );
      if (approveDestructiveActions == null || (await approveDestructiveActions(destructiveActions))) {
        shouldUpdateState = true;
      } else {
        shouldUpdateState = false;
      }
    }
    if (shouldUpdateState) {
      getStore().dispatch({
        type: BulkAssemblyActionType.BULK_ASSEMBLY_STATE_UPDATED,
        assemblyId: this.assemblyId,
        procedure: resolvedState,
      });
    }
    return shouldUpdateState;
  }

  getFragmentModalFragments(fragment: NormalizedFragment): Promise<FragmentModalFragment> {
    let color;
    let binName;
    if (isBAId(fragment.bin)) {
      const binCid = fragment.bin.id;
      const bin = this.state.binsByCid[binCid] as BulkAssemblyFragmentBin;
      color = bin.color;
      binName = bin.name;
    }
    const {polymer, polymerKind, polymerVersionDbId} = polymerDataFromFragment(fragment);
    assert(isBAId(polymer), 'Expected sequence or protein id to be resolved');

    return constructFragmentData({
      polymerId: polymer.id,
      polymerKind,
      expectedVersionDbId: polymerVersionDbId,
      showPrimerSearch: this.enableSelectPreferredPrimers(fragment.bin),
      endpoints: {
        start: fragment.start,
        end: fragment.end,
      },
      color,
      orientation: isBAOrientation(fragment.orientation) ? fragment.orientation.value : undefined,
      binName,
      preferredPrimer5Id: isBAId(fragment.preferredPrimer5) ? fragment.preferredPrimer5.id : null,
      preferredPrimer3Id: isBAId(fragment.preferredPrimer3) ? fragment.preferredPrimer3.id : null,
      cid: fragment.cid,
      binCid: isBAId(fragment.bin) ? fragment.bin.id : null,
      binIndex: this.getBinIndexFromFragment(fragment),
      constructFragmentIndex: this.assemblySelector.getConstructFragmentIndex(fragment),
      restrictionEnzyme5: fragment.restrictionEnzyme5,
      restrictionEnzyme3: fragment.restrictionEnzyme3,
      canSpecifyEndpoints: this.canSpecifyEndpoints(),
      canUpdateRestrictionEnzymes: this.canUpdateRestrictionEnzymes(
        isBAId(fragment.bin) ? fragment.bin.id : null
      ),
      constructTopology: this.topology,
      numFragmentBins: this.assemblySelector.getNumFragmentBins(),
      assemblyMethod: this.assemblySelector.getMethod(),
      fragmentProductionMethod: this.assemblySelector.getFragmentProductionMethodForBin(fragment.bin),
    });
  }

  enableSelectPreferredPrimers(binValue: BAIdValue | BARawTextValue): boolean {
    return (
      this.assemblySelector.getFragmentProductionMethodForBin(binValue) ===
      FragmentProductionMethod.PRIMER_PAIR
    );
  }

  /**
   * Whether or not the user can specify fragment endpoints. e.g. in homology assembly, we always consider
   * the entire sequence.
   */
  canSpecifyEndpoints(): boolean {
    return methodCanSpecifyEndpoints(this.state.method.type);
  }

  /**
   * Whether or not the user can update restriction enzymes for fragments in a particular bin.
   */
  canUpdateRestrictionEnzymes(binCid: string | null): boolean {
    let fragmentProductionMethod: FragmentProductionMethod;
    if (binCid == null) {
      fragmentProductionMethod = this.assemblySelector.getMethod().parameters.defaultFragmentProductionMethod;
    } else {
      const fragmentBin = this.state.binsByCid[binCid];
      assert(isBulkAssemblyFragmentBin(fragmentBin), 'Expected bin to be a fragment bin');
      fragmentProductionMethod = fragmentBin.fragmentProductionMethod;
    }
    return (
      this.assemblySelector.getMethod().type === BulkAssemblyMethod.GIBSON &&
      fragmentProductionMethod === FragmentProductionMethod.EXISTING_CUT_SITES
    );
  }

  /**
   * Whether or not the user can edit the contents of the assembly.
   */
  isReadOnly(): boolean {
    return (
      this.isArchived ||
      this.isFinalized ||
      !this.permissions.writable ||
      !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT})
    );
  }

  /**
   * Whether or not the assembly method can create primers (either by design or by user input).
   */
  canCreatePrimers(): boolean {
    return ![BulkAssemblyMethod.HOMOLOGY, BulkAssemblyMethod.CONCATENATION].includes(this.state.method.type);
  }

  /**
   * Whether or not the assembly method can use homology regions
   */
  canMethodUseHomologyRegions(): boolean {
    return [BulkAssemblyMethod.GIBSON, BulkAssemblyMethod.HOMOLOGY].includes(this.state.method.type);
  }

  /**
   * Derives the overall assembly fragment production method for the assembly.
   */
  getFragmentProductionMethod(): DisplayFragmentProductionMethod {
    const fragmentProductionMethods = new Set(
      this.assemblySelector
        .getFragmentBins()
        .map((fragmentBin) => this.assemblySelector.getFragmentProductionMethodForBin(baId(fragmentBin.cid)))
    );
    assert(fragmentProductionMethods.size !== 0, 'Unexpectedly encountered no fragment bins');
    if (fragmentProductionMethods.size === 1) {
      return fragmentProductionMethods.values().next().value;
    } else {
      return DisplayOnlyFragmentProductionMethod.VARIABLE;
    }
  }

  getNextFragmentBin(fragmentBin: BulkAssemblyBin): BulkAssemblyFragmentBin {
    assert(isBulkAssemblyFragmentBin(fragmentBin), 'Expected to receive only fragment bins');
    const currentBinIndex = this.assemblySelector.getFragmentBins().indexOf(fragmentBin);
    // TODO(BNCH-59975) Support linear constructs
    const nextBinIndex = (currentBinIndex + 1) % this.assemblySelector.getFragmentBins().length;
    return this.assemblySelector.getFragmentBins()[nextBinIndex];
  }

  getConstructsByCid(): {[cid: string]: NormalizedConstruct} {
    return groupByUniqueKey(this.state.constructs, (construct) => construct.cid);
  }

  /**
   * For finalized assemblies, success messages should be replaced with simply a check mark
   * rather than a message like "Ready to assemble".
   */
  prepareValidationMessages(
    messages: Array<BulkAssemblyValidationMessage>
  ): Array<BulkAssemblyValidationMessage> {
    if (!this.isFinalized) {
      return messages;
    }
    return messages.map((message) =>
      message.status === ValidationStatus.SUCCESS ? {status: ValidationStatus.SUCCESS, message: ''} : message
    );
  }

  private filterByValidationStatus<T extends {validationMessages: BulkAssemblyValidationMessages}>(
    items: Array<T>,
    validationStatusFilter: ValidationStatus | null
  ): Array<T> {
    return items.filter(({validationMessages}) => {
      if (validationStatusFilter == null || validationMessages === LOADING) {
        // If there is no filter applied or validation data is still loading, do not filter out.
        return true;
      } else if (validationStatusFilter === ValidationStatus.SUCCESS) {
        // Validation messages may be empty for "success" - just check that there are no non-success statuses.
        return validationMessages.every(({status}) => status === ValidationStatus.SUCCESS);
      } else {
        return validationMessages.some(({status}) => status === validationStatusFilter);
      }
    });
  }

  getTableFragments(): Array<TableFragment> {
    const filteredFragments = this.filterByValidationStatus(
      this.state.fragments.map((normalizedFragment) => this.getTableFragment(normalizedFragment)),
      this.fragmentStatusFilter
    );
    // Display placeholder row when there are no fragments.
    if (filteredFragments.length === 0) {
      return [this.getTableFragment(getEmptyNormalizedFragment())];
    }
    return filteredFragments;
  }

  private getPolymerNameForBAValue(polymer: BAIdValue | BARawTextValue): string | null {
    return isBAId(polymer) ? this.polymerData.getName(polymer.id) : null;
  }

  private getPolymerDataForBAValues({
    polymer,
    polymerVersionDbId,
  }: {
    polymer: BAIdValue | BARawTextValue;
    polymerVersionDbId: number | null;
  }): BasicPolymerInfo | null {
    return isBAId(polymer) && polymerVersionDbId != null
      ? this.polymerData.getBasicPolymerInfo(polymer.id, polymerVersionDbId)
      : null;
  }

  getBinIndexFromFragment(fragment: NormalizedFragment): number | null {
    const bin = fragment.bin;
    if (isBAId(bin) && this.state.binsByCid[bin.id] != null) {
      return this.getBinCidOrdering().findIndex((cid) => cid === bin.id);
    }
    return null;
  }

  private getEndpointDataForDisplay(
    fragment: NormalizedFragment
  ): Pick<TableFragment, Side | 'length' | 'enzymeCutsiteData5' | 'enzymeCutsiteData3'> {
    const blankEndpointData = {
      start: {text: '', locked: true},
      end: {text: '', locked: true},
      length: {text: ''},
      enzymeCutsiteData5: null,
      enzymeCutsiteData3: null,
    };
    const polymerData = this.getPolymerDataForBAValues(polymerDataFromFragment(fragment));
    const {restrictionEnzymeId5, restrictionEnzymeId3} =
      this.assemblySelector.getRestrictionEnzymeIdsForFragment(fragment);
    if (!polymerData) {
      return blankEndpointData;
    }
    const modelToDisplayConverters = getModelToDisplayConverters(polymerData.startIndex);
    const appliedOrientation = getOrientationWithFallback(fragment.orientation);
    const restrictionEnzymeInfo5 = getRestrictionEnzymeInfo(
      restrictionEnzymeId5,
      polymerData.residues,
      polymerData.circular
    );
    const restrictionEnzymeInfo3 = getRestrictionEnzymeInfo(
      restrictionEnzymeId3,
      polymerData.residues,
      polymerData.circular
    );
    const preferredRestrictionEnzymeId = getPreferredRestrictionEnzymeId(this.assemblySelector.getMethod());
    const areRestrictionEnzymesRequired = areEnzymesRequired(
      this.assemblySelector.getConstructFragmentIndex(fragment),
      getRestrictionEnzymeInfo(preferredRestrictionEnzymeId, polymerData.residues, polymerData.circular),
      this.assemblySelector.getSequenceTopology(),
      this.assemblySelector.getNumFragmentBins(),
      this.assemblySelector.getFragmentProductionMethodForBin(fragment.bin)
    );
    const {startErrorMessage, endErrorMessage, length} = validateEndpoints(
      fragment,
      polymerData.residues.length,
      polymerData.startIndex,
      appliedOrientation,
      restrictionEnzymeInfo5,
      restrictionEnzymeInfo3,
      this.canSpecifyEndpoints(),
      polymerData.circular,
      areRestrictionEnzymesRequired
    );
    const formattedStart = formatBAEndpoint(fragment.start, modelToDisplayConverters.start);
    const formattedEnd = formatBAEndpoint(fragment.end, modelToDisplayConverters.end);
    return {
      start: {
        text: getValueForOrientation(appliedOrientation, formattedStart, formattedEnd),
        error: startErrorMessage,
        locked: !this.canSpecifyEndpoints(),
      },
      end: {
        text: getValueForOrientation(appliedOrientation, formattedEnd, formattedStart),
        error: endErrorMessage,
        locked: !this.canSpecifyEndpoints(),
      },
      length: {
        text: length == null ? '' : String(length),
      },
      enzymeCutsiteData5:
        restrictionEnzymeInfo5.cutsiteIndices != null
          ? {
              restrictionEnzymeId: restrictionEnzymeInfo5.restrictionEnzymeId,
              cutsites: restrictionEnzymeInfo5.cutsiteIndices,
              modelToDisplayConverters,
              orientation: appliedOrientation,
            }
          : null,
      enzymeCutsiteData3:
        restrictionEnzymeInfo3.cutsiteIndices != null
          ? {
              restrictionEnzymeId: restrictionEnzymeInfo3.restrictionEnzymeId,
              cutsites: restrictionEnzymeInfo3.cutsiteIndices,
              modelToDisplayConverters,
              orientation: appliedOrientation,
            }
          : null,
    };
  }

  private formatRestrictionEnzymeForDisplay(fragment: NormalizedFragment): {
    restrictionEnzyme5: RestrictionEnzymeTableInfo;
    restrictionEnzyme3: RestrictionEnzymeTableInfo;
  } {
    // Short-circuit if we're not in a Golden Gate assembly.
    const method = this.assemblySelector.getMethod();
    if (!isRestrictionEnzymeBulkAssemblyType(method)) {
      return {
        restrictionEnzyme5: {
          text: '',
          locked: true,
        },
        restrictionEnzyme3: {
          text: '',
          locked: true,
        },
      };
    }

    const fragmentProductionMethod = this.assemblySelector.getFragmentProductionMethodForBin(fragment.bin);
    const {restrictionEnzymeId5, restrictionEnzymeId3} =
      this.assemblySelector.getRestrictionEnzymeIdsForFragment(fragment);
    return {
      restrictionEnzyme5: {
        text:
          restrictionEnzymeId5 != null && restrictionEnzymeId5 !== NO_ENZYME_SENTINEL
            ? getEnzyme(restrictionEnzymeId5 || method.parameters.defaultRestrictionEnzymeId).name
            : 'No enzyme selected',
        locked: fragmentProductionMethod !== FragmentProductionMethod.EXISTING_CUT_SITES,
      },
      restrictionEnzyme3: {
        text:
          restrictionEnzymeId3 != null && restrictionEnzymeId3 !== NO_ENZYME_SENTINEL
            ? getEnzyme(restrictionEnzymeId3 || method.parameters.defaultRestrictionEnzymeId).name
            : 'No enzyme selected',
        locked: fragmentProductionMethod !== FragmentProductionMethod.EXISTING_CUT_SITES,
      },
    };
  }

  private getFragmentRowDescriptor(fragment: NormalizedFragment): string {
    const matchingFragmentIndex = this.getTableFragments().findIndex((f) => f.cid === fragment.cid);
    if (matchingFragmentIndex === -1) {
      return 'hidden';
    }
    return (matchingFragmentIndex + 1).toString();
  }

  private formatPolymerForDisplay(polymer: BAIdValue | BARawTextValue): {text: string; id?: string} {
    if (isBAId(polymer)) {
      return {
        text: this.getPolymerNameForBAValue(polymer) ?? 'Loading...',
        id: polymer.id,
      };
    } else {
      return {text: polymer.rawText};
    }
  }

  private getTableFragment(fragment: NormalizedFragment): TableFragment {
    const {polymer, polymerVersionDbId} = polymerDataFromFragment(fragment);
    const polymerInfo = this.getPolymerDataForBAValues({polymer, polymerVersionDbId});
    let bin: TableFragment['bin'];
    if (!isBAId(fragment.bin) && !isEmptyBAValue(fragment.bin)) {
      bin = {
        text: fragment.bin.rawText,
        error: 'Bin is not recognized',
      };
    } else if (isBAId(fragment.bin)) {
      bin = {text: this.state.binsByCid[fragment.bin.id]?.name ?? ''};
    } else {
      bin = {text: ''};
    }

    let orientation: TableFragment['orientation'];
    const lockOrientation = !polymerInfo?.circular && this.canSpecifyEndpoints();
    if (isEmptyBAValue(fragment.orientation)) {
      orientation = {text: '', locked: lockOrientation};
    } else if (!isBAOrientation(fragment.orientation)) {
      orientation = {
        text: fragment.orientation.rawText,
        error: 'Orientation must be "Forward" or "Reverse".',
        locked: lockOrientation,
      };
    } else {
      orientation = {
        text: BULK_ASSEMBLY_ORIENTATION_NAME[fragment.orientation.value],
        locked: lockOrientation,
      };
    }
    const fragmentProductionMethod = this.assemblySelector.getFragmentProductionMethodForBin(fragment.bin);

    const result: Omit<TableFragment, 'validationMessages'> = {
      ...this.getEndpointDataForDisplay(fragment),
      cid: fragment.cid,
      bin,
      orientation,
      ...this.formatRestrictionEnzymeForDisplay(fragment),
      fragmentProductionMethod: {
        text: BULK_ASSEMBLY_METHOD_TO_FRAGMENT_PRODUCTION_METHOD_TO_DISPLAY_PARAMS[
          this.assemblySelector.getMethod().type
        ][fragmentProductionMethod]!.display,
      },
      polymer: this.formatPolymerForDisplay(polymer),
      polymerVersionId: polymerVersionDbId,
      preferredPrimer5: {
        ...this.formatPolymerForDisplay(fragment.preferredPrimer5),
        locked: !this.enableSelectPreferredPrimers(fragment.bin),
      },
      preferredPrimer3: {
        ...this.formatPolymerForDisplay(fragment.preferredPrimer3),
        locked: !this.enableSelectPreferredPrimers(fragment.bin),
      },
    };
    return {
      ...result,
      validationMessages: this.formatValidationMessagesForFragment(fragment.cid, result, polymerInfo),
    };
  }

  private formatValidationMessagesForFragment(
    fragmentCid: string,
    displayFragment: Omit<TableFragment, 'validationMessages'>,
    polymerInfo: BasicPolymerInfo | null
  ): BulkAssemblyValidationMessages {
    const backendValidationResult = this.validationResults.fragmentResultsByCid[fragmentCid] ?? null;
    if (backendValidationResult === LOADING) {
      return LOADING;
    }
    const frontendValidationMessages: Array<BulkAssemblyValidationMessage> = [];
    // Everything except cid could have an error message.
    const {bin, start, end, orientation} = displayFragment;
    for (const data of [bin, start, end, orientation]) {
      if (data?.error != null) {
        frontendValidationMessages.push({status: ValidationStatus.ERROR, message: data.error});
      }
    }
    const fragment = this.state.fragments.find((fragment) => fragment.cid === fragmentCid);
    if (fragment != null) {
      const fragmentBin = isBAId(fragment.bin) ? this.state.binsByCid[fragment.bin.id] : null;
      assert(
        fragmentBin == null || isBulkAssemblyFragmentBin(fragmentBin),
        'Expected to receive fragment bin'
      );
      const frontendFragmentValidator = new AssemblyFrontendFragmentValidator(
        this.state.method,
        fragment,
        fragmentBin
      );
      frontendValidationMessages.push(
        ...frontendFragmentValidator.getAssemblyFrontendFragmentValidationErrors(polymerInfo)
      );
    }

    return frontendValidationMessages.length
      ? frontendValidationMessages
      : this.prepareValidationMessages(backendValidationResult?.validationMessages ?? []);
  }

  handleAddNewFragmentBin(): void {
    const cid = uuidv4();
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        draft.binCidOrdering.push(cid);
        draft.binsByCid[cid] = {
          cid,
          name: generateNewBinName(
            Object.values(this.state.binsByCid),
            this.assemblySelector.getMethod().type
          ),
          color: getNewBinColor(Object.values(this.state.binsByCid)),
          binType: BulkAssemblyBinType.FRAGMENT,
          fragmentProductionMethod:
            this.assemblySelector.getMethod().parameters.defaultFragmentProductionMethod,
        };
      })
    );
  }

  handleAddNewSpacerBin(): void {
    const cid = uuidv4();
    assert(
      isSpacerSupportingBulkAssemblyType(this.assemblySelector.getMethod().type),
      'Tried to update spacer for unsupported assembly type'
    );
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        draft.binCidOrdering.push(cid);
        draft.binsByCid[cid] = {
          cid,
          name: capitalize(
            METHOD_TO_SPACER_NAME[this.assemblySelector.getMethod().type as SpacerSupportingBulkAssemblyType]
          ),
          residues: '',
          binType: BulkAssemblyBinType.SPACER,
        };
      })
    );
  }

  /**
   * Get all names of fragments where that name is not unique in that fragment's bin, grouped by bin cid.
   */
  private getDuplicateFragmentNamesByBin(): {[binCid: string]: Set<string>} {
    const fragmentsWithBin = this.state.fragments.filter((fragment) => isBAId(fragment.bin));
    const fragmentsByBin = groupBy(fragmentsWithBin, (fragment) => (fragment.bin as BAIdValue).id);

    // Create an empty Set for each bin, to hold any duplicate names we encounter later.
    const duplicateFragmentNamesByBinCid = objectFromEntries(
      Object.keys(fragmentsByBin).map((binCid) => [binCid, new Set<string>()])
    );

    for (const [binCid, fragments] of Object.entries(fragmentsByBin)) {
      const namesInBin = new Set<string>();
      for (const fragment of fragments) {
        const polymer = isProteinBasedAssembly(this.assemblySelector.getMethod())
          ? fragment.protein
          : fragment.sequence;
        const fragmentName = this.getPolymerNameForBAValue(polymer);
        if (fragmentName != null && fragmentName !== '') {
          if (namesInBin.has(fragmentName)) {
            duplicateFragmentNamesByBinCid[binCid].add(fragmentName);
          } else {
            namesInBin.add(fragmentName);
          }
        }
      }
    }

    return duplicateFragmentNamesByBinCid;
  }

  /**
   * Get all fragments that have had both a polymer and a bin specified, grouped by bin.
   */
  private getSpecifiedFragmentInfosByBinCid(): {[binCid: string]: Array<SpecifiedFragmentInfo>} {
    const specifiedFragmentInfos = this.state.fragments.flatMap<SpecifiedFragmentInfo, NormalizedFragment>(
      (fragment: NormalizedFragment): Array<SpecifiedFragmentInfo> => {
        const polymer = isProteinBasedAssembly(this.assemblySelector.getMethod())
          ? fragment.protein
          : fragment.sequence;
        if (!isBAId(polymer) || !isBAId(fragment.bin)) {
          return [];
        }
        // TODO: this function is already called within `getFragmentDisplayName`, we should remove this
        let polymerName = this.getPolymerNameForBAValue(polymer);
        if (polymerName == null) {
          return [];
        }
        const {start, end} = this.getEndpointDataForDisplay(fragment);
        polymerName = this.getFragmentDisplayName(fragment, fragment.bin.id);

        const errors: Array<string> = [];

        if (this.canSpecifyEndpoints()) {
          if (start.text === '') {
            errors.push(`Add a start value to ${polymerName} to autopopulate.`);
          } else if (start.error != null) {
            errors.push(`Fix the invalid start value for ${polymerName} to autopopulate.`);
          }

          if (end.text === '') {
            errors.push(`Add an end value to ${polymerName} to autopopulate.`);
          } else if (end.error != null) {
            errors.push(`Fix the invalid end value for ${polymerName} to autopopulate.`);
          }
        }

        if (!isBAOrientation(fragment.orientation)) {
          errors.push(`Fix the invalid orientation for ${polymerName} to autopopulate.`);
        }

        const fragmentValidation = this.validationResults.fragmentResultsByCid[fragment.cid];
        if (
          fragmentValidation != null &&
          fragmentValidation !== LOADING &&
          fragmentValidation.validationMessages.filter((message) => message.status === ValidationStatus.ERROR)
            .length > 0
        ) {
          errors.push(`Resolve error(s) for ${polymerName} to autopopulate.`);
        }

        return [
          {
            fragmentCid: fragment.cid,
            binCid: fragment.bin.id,
            polymerName,
            errors,
          },
        ];
      }
    );
    return groupBy(specifiedFragmentInfos, (fragment) => fragment.binCid);
  }

  /**
   * Update the name of the given bin in the store.
   * @param binCid Id of the bin to update.
   * @param name New name of the bin.
   */
  updateBinName(binCid: string, name: string): void {
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        draft.binsByCid[binCid].name = name;
      })
    );
  }

  /**
   * Delete the given bin from the store.
   * @param index Index of the bin to delete.
   */
  deleteBin(index: number): void {
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        const [binCid] = draft.binCidOrdering.splice(index, 1);
        delete draft.binsByCid[binCid];
      })
    );
  }

  /**
   * Update the fragment production method on the given bin.
   * @param binCid Id of the bin to update.
   * @param fragmentProductionMethod New fragment production method for the bin.
   */
  async updateBinFragmentProductionMethod(
    binCid: string,
    fragmentProductionMethod: FragmentProductionMethod,
    approveDestructiveActions: (destructiveActions: DestructiveActionsForApproval) => Promise<boolean>
  ): Promise<void> {
    if (
      assertAndContinue(
        isBulkAssemblyFragmentBin(this.state.binsByCid[binCid]),
        'Tried to set fragment production method on a non-fragment bin'
      ) &&
      assertAndContinue(
        this.state.method.type === BulkAssemblyMethod.GOLDEN_GATE ||
          this.state.method.type === BulkAssemblyMethod.GIBSON,
        `Tried to set fragment production method in a ${METHOD_TO_DISPLAY[this.state.method.type]} assembly`
      )
    ) {
      const didPerformUpdate = await this.resolveAndUpdateState(
        produce(this.state, (draft) => {
          (draft.binsByCid[binCid] as BulkAssemblyFragmentBin).fragmentProductionMethod =
            fragmentProductionMethod;
        }),
        approveDestructiveActions
      );
      if (didPerformUpdate) {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_BIN_SETTINGS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BIN_LAYOUT,
          assemblyId: this.assemblyId,
        });
      }
    }
  }

  /**
   * Update the bases associated with the given spacer bin.
   * @param binCid Id of the bin to update.
   * @param residues New residues of the bin.
   */
  updateSpacerBinBases(binCid: string, residues: string): void {
    if (
      assertAndContinue(
        isBulkAssemblySpacerBin(this.state.binsByCid[binCid]),
        'Tried to update spacer bases for non-spacer bin'
      )
    ) {
      this.resolveAndUpdateState(
        produce(this.state, (draft) => {
          (draft.binsByCid[binCid] as BulkAssemblySpacerBin).residues = residues;
        })
      );
    }
  }

  /**
   * Generates a list of lists of fragments, in fragment bin order, where all those fragments are eligible
   * for autopopulate.
   */
  getOrderedFragmentListsForAutopopulate(): Array<Array<SpecifiedFragmentInfo>> {
    const validSpecifiedFragmentInfosByBinId = objectFromEntries(
      Object.entries(this.specifiedFragmentInfosByBinCid).map(([binCid, specifiedFragmentInfos]) => [
        binCid,
        specifiedFragmentInfos.filter((specifiedFragmentInfo) =>
          this.isValidFragmentForAutopopulate(specifiedFragmentInfo.fragmentCid)
        ),
      ])
    );
    return this.assemblySelector
      .getFragmentBins()
      .map((bin) => validSpecifiedFragmentInfosByBinId[bin.cid] ?? []);
  }

  handleAutopopulateConstructs(): void {
    if (this.getAutopopulateButtonStatus() == null) {
      const orderedFragmentsLists = this.getOrderedFragmentListsForAutopopulate();
      const product: Array<Array<SpecifiedFragmentInfo>> = cartesianProduct(orderedFragmentsLists);

      const constructUpdates: Array<ConstructUpdate> = product.map(
        (fragments): ConstructUpdate => ({
          cid: uuidv4(),
          constructFragmentsByBinId: objectFromEntries(
            fragments.map((fragment) => [fragment.binCid, {fragment: baId(fragment.fragmentCid)}])
          ),
        })
      );

      this.handleTableConstructsUpdate(constructUpdates);
    }
  }

  handleMoveBin(sourceIndex: number, targetIndex: number): void {
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        // Remove the bin at sourceIndex...
        const [sourcebinCid] = draft.binCidOrdering.splice(sourceIndex, 1);
        if (sourceIndex > targetIndex) {
          // ...and insert it after the bin at targetIndex;
          draft.binCidOrdering.splice(targetIndex + 1, 0, sourcebinCid);
        } else {
          // ... and insert it after the target index, accounting for that the target index has shifted within
          // binCidOrdering upon removing sourceItem
          draft.binCidOrdering.splice(targetIndex, 0, sourcebinCid);
        }
      })
    );
  }

  getBinCount(): number {
    return this.state.binCidOrdering.length;
  }

  /**
   * Returns the list of bin cids in assembly order.
   */
  getBinCidOrdering(): Array<string> {
    return this.state.binCidOrdering;
  }

  /**
   * Get an array of display bins with all data needed to display a bin card.
   */
  getBinsForDisplay(): Array<DisplayBin> {
    return this.state.binCidOrdering.map((binCid) => {
      const bin = this.state.binsByCid[binCid];
      if (isBulkAssemblyFragmentBin(bin)) {
        const numBinFragments = this.getFilledOutFragments().filter(
          (fragment) => isBAId(fragment.bin) && fragment.bin.id === bin.cid
        ).length;
        return {
          ...bin,
          lockName: false,
          numBinFragments,
        };
      } else {
        return bin;
      }
    });
  }

  generateConstructName(construct: NormalizedConstruct): string {
    const nameFragments: Array<string> = [];
    for (const {cid: binCid} of this.assemblySelector.getFragmentBins()) {
      const constructFragment = construct.constructFragmentsByBinId[binCid];
      if (!constructFragment) {
        return '';
      }
      if (constructFragment.fragment.type === BAValueType.RAW_TEXT) {
        if (constructFragment.fragment.rawText === SKIP_BIN_SENTINEL) {
          continue;
        }
        return '';
      }
      const fragment = this.fragmentsByCid[constructFragment.fragment.id];
      if (fragment == null) {
        return '';
      }
      const polymer = isProteinBasedAssembly(this.assemblySelector.getMethod())
        ? fragment.protein
        : fragment.sequence;
      const polymerName = this.getPolymerNameForBAValue(polymer);
      if (polymerName == null) {
        return '';
      }
      if (
        this.duplicateFragmentNamesByBin[binCid] != null &&
        this.duplicateFragmentNamesByBin[binCid].has(polymerName) &&
        isBANumber(fragment.start) &&
        isBANumber(fragment.end)
      ) {
        const {start, end} = this.getEndpointDataForDisplay(fragment);
        const fragmentName = `${polymerName}-${start.text}-${end.text}`;
        nameFragments.push(fragmentName);
      } else {
        nameFragments.push(polymerName);
      }
    }
    return nameFragments.join('-');
  }

  getBinColorByFragmentCid(fragmentCid: string): string | null {
    const fragment = this.fragmentsByCid[fragmentCid];
    const bin = this.assemblySelector.getBinForFragment(fragment);
    return bin != null ? bin.color : null;
  }

  getFragmentResults(
    validationResult: BulkAssemblyConstructValidationResult
  ): Array<{start: number; end: number; color: string; homologyRegionOtherColor: string | null}> {
    let fragmentResults: Array<{
      start: number;
      end: number;
      color: string;
      homologyRegionOtherColor: string | null;
    }> = [];
    if (this.canMethodUseHomologyRegions()) {
      const hasNoExplicitOverlaps = validationResult.constructFragments.every(
        (constructFragment) =>
          constructFragment.overlap5End == null && constructFragment.overlap3Start == null
      );
      for (const i of range(0, validationResult.constructFragments.length - 1)) {
        const thisFragment = validationResult.constructFragments[i];
        let nextIndex: number | null;
        if (this.topology === SequenceTopology.CIRCULAR) {
          nextIndex = sequtil.mod(i + 1, validationResult.constructFragments.length);
        } else if (i !== validationResult.constructFragments.length - 1) {
          nextIndex = i + 1;
        } else {
          nextIndex = null;
        }
        const nextFragment = nextIndex != null ? validationResult.constructFragments[nextIndex] : null;

        let fragmentStart: number;
        let junctionStart: number;
        let junctionEnd: number | null;
        if (hasNoExplicitOverlaps) {
          // All junctions are homology junctions, where the fragment start/end values have been adjusted to
          // exclude the overlaps instead of specifying the overlaps explicitly
          fragmentStart = thisFragment.start;
          junctionStart = thisFragment.end;
          junctionEnd = nextFragment?.start ?? null;
        } else {
          fragmentStart = thisFragment.overlap5End ?? thisFragment.start;
          junctionStart = thisFragment.overlap3Start ?? thisFragment.end;
          junctionEnd = nextFragment?.overlap5End ?? nextFragment?.start ?? null;
        }

        // Make a FragmentResult for the non-overlapping region part of this fragment...
        fragmentResults.push({
          start: fragmentStart,
          end: junctionStart,
          color: this.getBinColorByFragmentCid(thisFragment.fragmentId)!,
          homologyRegionOtherColor: null,
        });
        // ...and for the 3' homology region.
        if (nextFragment != null && junctionEnd != null) {
          fragmentResults.push({
            start: junctionStart,
            end: junctionEnd,
            color: this.getBinColorByFragmentCid(thisFragment.fragmentId)!,
            homologyRegionOtherColor: this.getBinColorByFragmentCid(nextFragment.fragmentId),
          });
        }
      }
    } else {
      // None of the junctions are homology junctions, and if there are PCR junctions they're in a Golden
      // Gate assembly where we care about sticky ends instead of overlaps.
      fragmentResults = validationResult.constructFragments.map(({start, end, fragmentId}) => ({
        start,
        end,
        color: this.getBinColorByFragmentCid(fragmentId)!,
        homologyRegionOtherColor: null,
      }));
    }
    fragmentResults.sort((fr1, fr2) => fr1.start - fr2.start);
    return fragmentResults;
  }

  private sequencePolymerInfoFromValidationResult(
    sequenceValidationResult: BulkAssemblyConstructValidationResult & {
      polymer: AssembledSequenceConstruct;
    },
    name: string
  ): SequencePolymerInfo {
    const sequenceAttributes: Partial<SequenceAttributes> = {
      name,
      bases: sequenceValidationResult.polymer.bases,
      circular: sequenceValidationResult.polymer.circular,
      type: SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[sequenceValidationResult.polymer.sequenceType],
      nucleotideType: sequenceValidationResult.polymer.nucleotideType,
      modifiedNucleotides: sequenceValidationResult.polymer.modifiedNucleotides.map(
        ({position, sugarMonomerId, phosphateMonomerId, baseMonomerId}) => ({
          position,
          sugarMonomerId,
          phosphateMonomerId,
          baseMonomerId,
        })
      ),
      id: TEMP_SEQUENCE_ID,
      finalized: false,
    };
    const biojsSequence = new BioJSSequence(sequenceAttributes);

    const annotations = sequenceValidationResult.polymer.annotations.map(
      ({name, start, end, strand, color}) => new Annotation({name, start, end, strand, color})
    );
    biojsSequence.annotations().reset(annotations);

    const translations = sequenceValidationResult.polymer.translations.flatMap(({strand, regions}, i) =>
      regions.map(
        ({start, end}) =>
          new Translation({
            start,
            end,
            strand,
            translationGroupId: i,
          })
      )
    );
    biojsSequence.translations().reset(translations);
    biojsSequence.set(
      'translationGroups',
      sequenceValidationResult.polymer.translations.map(
        ({name, codonTranslationTable, coerceStartCodonToMethionine, color}, i) => ({
          id: i,
          name,
          notes: '',
          customFields: [],
          codonTranslationTable,
          coerceStartCodonToMethionine,
          color,
        })
      )
    );

    const fragmentResults = this.getFragmentResults(sequenceValidationResult);
    // @ts-expect-error: it's OK to add models to a collection as a POJO array
    biojsSequence.fragmentResults().reset(fragmentResults);

    for (const primerValidationResult of sequenceValidationResult.primerValidationResults) {
      const {primer} = primerValidationResult;
      if (!primer) {
        // No primer could be generated, so don't show anything.
        continue;
      }
      const sequencePrimer = new SequencePrimer({
        name: primer.oligoContents.name,
        bases: primer.oligoContents.bases,
        strand: primerValidationResult.sequencePrime === SequencePrime.FIVE ? 1 : -1,
        // NOTE: primer will always have dashed outline (i.e. "ghost primer") since the numeric `id` attribute is not set,
        // so it is detected as unsaved.
        primer_api_id: primer.oligoId,
        // NOTE: the default primer color is randomized, so it may not even be consistent across different views
      });
      sequencePrimer.setBindPosition(biojsSequence, primer.bindPosition, primer.overhangLength);
      biojsSequence.get('sequencePrimers').add(sequencePrimer);
    }

    const sequence = new Sequence({fileState: null, id: TEMP_SEQUENCE_ID, _sequence: biojsSequence}, {});
    const fileState = new DisplaySequenceFileState({sequence});
    sequence.set({fileState});
    return {
      kind: 'sequence',
      ref: {
        current: sequence,
      },
    };
  }

  private proteinPolymerInfoFromValidationResult(
    proteinValidationResult: BulkAssemblyConstructValidationResult & {
      polymer: AssembledProteinConstruct;
    },
    name: string
  ): ProteinPolymerInfo {
    const proteinId = uuidv4();
    const protein = new Protein({id: proteinId});
    const proteinVersion = {
      id: 0,
      aminoAcids: proteinValidationResult.polymer.aminoAcids,
      annotations: proteinValidationResult.polymer.annotations.map(({name, start, end, color}, idx) => ({
        id: idx,
        name,
        start,
        end,
        color,
        type: '',
      })),
      createdAt: '',
      name,
      parentId: null,
      structures: [],
      fragments: this.getFragmentResults(proteinValidationResult),
    };
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_FETCH_VERSIONS_COMPLETED,
      versions: [proteinVersion],
      proteinId,
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
      protein: {
        alignments: [],
        linkedAlignments: [],
        archiveRecord: null,
        authors: [],
        bioentityAliasInfos: [],
        created_at: '',
        modified_at: '',
        displayLabel: '',
        editURL: '',
        id: proteinId,
        isLatest: true,
        name,
        permissions: {
          admin: false,
          appendable: false,
          archivable: false,
          canEditNamesAndAliases: true,
          deletable: false,
          movable: false,
          polymerFeaturesEditable: false,
          polymerResiduesEditable: false,
          readable: true,
          registryIdsChangeable: false,
          unarchivable: false,
          unregisterable: false,
          writable: false,
        },
        schemaValidationState: null,
        tagSchema: null,
        tags: [],
        version: proteinVersion,
      },
      proteinId,
    });
    return {
      kind: 'protein',
      ref: {current: protein},
    };
  }

  /**
   * If possible, create a Sequence or Protein backbone model reference representing the given construct.
   */
  maybeCreatePreviewConstructPolymer(constructCid: string): SequencePolymerInfo | ProteinPolymerInfo | null {
    const validationResult = this.validationResults.constructResultsByCid[constructCid];
    if (!validationResult || validationResult === LOADING || validationResult.polymer == null) {
      return null;
    }
    const name = this.generateConstructName(this.getConstructsByCid()[constructCid]);
    const validationPolymer = validationResult.polymer;
    if (constructPolymerIsSequence(validationPolymer)) {
      return this.sequencePolymerInfoFromValidationResult(
        {...validationResult, polymer: validationPolymer},
        name
      );
    } else {
      return this.proteinPolymerInfoFromValidationResult(
        {...validationResult, polymer: validationPolymer},
        name
      );
    }
  }

  /**
   * If possible, create a Visual Polymer object  the given construct.
   */
  maybeCreatePreviewConstructVisualPolymer(
    validationResult: BulkAssemblyConstructValidationResult,
    construct: NormalizedConstruct
  ): VisualPolymer | null {
    if (validationResult.polymer == null) {
      return null;
    }
    const constructPolymerContent = validationResult.polymer;
    if (constructPolymerIsSequence(constructPolymerContent)) {
      // Collect unique enzyme Ids.
      const enzymeIds = [
        ...new Set(
          Object.values(construct.constructFragmentsByBinId).flatMap((constructFragment) => {
            const fragment = isBAId(constructFragment.fragment)
              ? this.state.fragments.find(
                  (fragment) => fragment.cid === (constructFragment.fragment as BAIdValue).id
                )
              : null;
            if (fragment == null) {
              return [];
            }
            const {restrictionEnzymeId5, restrictionEnzymeId3} =
              this.assemblySelector.getRestrictionEnzymeIdsForFragment(fragment);
            const result = [];
            if (restrictionEnzymeId5 != null && restrictionEnzymeId5 !== NO_ENZYME_SENTINEL) {
              result.push(restrictionEnzymeId5);
            }
            if (restrictionEnzymeId3 != null && restrictionEnzymeId3 !== NO_ENZYME_SENTINEL) {
              result.push(restrictionEnzymeId3);
            }
            return result;
          })
        ),
      ];
      const cutSites = enzymeIds.flatMap((enzymeId) =>
        getCuts(
          getEnzyme(enzymeId),
          constructPolymerContent.bases,
          constructPolymerContent.nucleotideType,
          SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[constructPolymerContent.sequenceType],
          constructPolymerContent.circular
        ).map(({position}) => ({position, enzymeId}))
      );
      return {
        circular: constructPolymerContent.circular,
        fragments: this.getFragmentResults(validationResult),
        length: constructPolymerContent.bases.length,
        startIndex: 1,
        cutSites,
        annotations: constructPolymerContent.annotations.map(({name, start, end, strand, color}) => ({
          name,
          start,
          end,
          color,
          strand,
        })),
        overlays: [],
      };
    } else {
      return {
        circular: false,
        fragments: this.getFragmentResults(validationResult),
        length: constructPolymerContent.aminoAcids.length,
        startIndex: 1,
        cutSites: [],
        annotations: constructPolymerContent.annotations.map(({name, start, end, color}) => ({
          name,
          start,
          end,
          color,
          strand: 1,
        })),
        overlays: [],
      };
    }
  }

  /**
   * Return constructs with all information needed to show them in the constructs tab.
   *
   * Skip constructs that are incomplete or have in-progress validation.
   */
  getPreviewConstructs(): Array<PreviewConstruct> {
    const fragmentNamesByFragmentCid = mapObject(this.fragmentsByCid, (normalizedFragment) => {
      const {polymer} = polymerDataFromFragment(normalizedFragment);
      return this.getPolymerNameForBAValue(polymer) ?? '';
    });
    return this.state.constructs.flatMap((construct) => {
      const validationResult = this.validationResults.constructResultsByCid[construct.cid];
      if (!validationResult || validationResult === LOADING) {
        return [];
      }

      const fragmentCids = Object.values(construct.constructFragmentsByBinId).flatMap((constructFragment) =>
        isBAId(constructFragment.fragment) ? [constructFragment.fragment.id] : []
      );
      const fragmentNames = fragmentCids.map((fragmentCid) => fragmentNamesByFragmentCid[fragmentCid]);

      return [
        {
          cid: construct.cid,
          name: this.generateConstructName(construct),
          visualPolymer: this.maybeCreatePreviewConstructVisualPolymer(validationResult, construct),
          fragmentCids,
          fragmentNames,
          primers: this.getPrimersForDisplay(validationResult),
          hasErrors: validationResult.validationMessages.some(
            (message) => message.status === ValidationStatus.ERROR
          ),
          hasWarnings: validationResult.validationMessages.some(
            (message) => message.status === ValidationStatus.WARNING
          ),
          outputPolymerId: this.getConstructOutputPolymerId(construct.cid),
        },
      ];
    });
  }

  /**
   * Return constructs with all information needed to show them in the constructs table.
   */
  getTableConstructs(): Array<TableConstruct> {
    const displayConstructs = this.state.constructs.map((construct) => this.getTableConstruct(construct));
    return this.filterByValidationStatus(displayConstructs, this.constructStatusFilter);
  }

  getConstructOutputPolymerId(constructCid: string): string | null {
    const validationResult = this.validationResults.constructResultsByCid[constructCid] ?? null;
    if (
      !this.isFinalized ||
      validationResult == null ||
      validationResult === LOADING ||
      validationResult.polymer == null
    ) {
      return null;
    }
    return constructPolymerIsSequence(validationResult.polymer)
      ? validationResult.polymer.createdSequenceId
      : validationResult.polymer.createdProteinId;
  }

  getTableConstruct(construct: NormalizedConstruct): TableConstruct {
    const validationResult = this.validationResults.constructResultsByCid[construct.cid] ?? null;
    const polymerInfo =
      validationResult != null && validationResult !== LOADING && validationResult.polymer != null
        ? validationResult.polymer
        : null;
    let polymerLength: number | null = null;
    let name: string | null = null;
    if (polymerInfo != null && constructPolymerIsSequence(polymerInfo)) {
      polymerLength = polymerInfo.bases.length;
      name = this.isFinalized ? polymerInfo.createdSequenceCurrentName : null;
    } else if (polymerInfo != null) {
      polymerLength = polymerInfo.aminoAcids.length;
      name = this.isFinalized ? polymerInfo.createdProteinCurrentName : null;
    }

    let constructFragmentValidationResults:
      | {[fragmentId: string]: ValidationConstructFragment}
      | LOADING
      | null;
    if (validationResult == null) {
      constructFragmentValidationResults = null;
    } else if (validationResult === LOADING) {
      constructFragmentValidationResults = LOADING;
    } else {
      constructFragmentValidationResults = groupByUniqueKey(
        validationResult.constructFragments,
        (constructFragment) => constructFragment.fragmentId
      );
    }

    const displayConstructFragmentByBinId = objectFromEntries(
      // First filter out any bin IDs that do not exist since there may be hanging IDs after bin deletions
      Object.entries(construct.constructFragmentsByBinId)
        .filter(([binCid, _]) => this.state.binsByCid[binCid] != null)
        .map(([binCid, constructFragment], binIndex) => {
          const nextBinCid = this.getNextFragmentBin(this.state.binsByCid[binCid]).cid;
          const nextConstructFragment = construct.constructFragmentsByBinId[nextBinCid];
          return [
            binCid,
            this.getConstructFragmentForDisplay(
              binCid,
              constructFragment,
              nextConstructFragment,
              constructFragmentValidationResults,
              polymerLength
            ),
          ];
        })
    );

    const result: Omit<TableConstruct, 'validationMessages'> = {
      ...construct,
      createdSequenceId: this.getConstructOutputPolymerId(construct.cid),
      name: name ?? this.generateConstructName(construct),
      tableConstructFragmentByBinId: displayConstructFragmentByBinId,
      canPreview: validationResult != null && validationResult !== LOADING,
    };

    return {
      ...result,
      validationMessages: this.formatValidationMessagesForConstruct(result),
    };
  }

  private hasNonEmptySpacers(): boolean {
    return (
      this.state.binCidOrdering
        .map((cid) => this.state.binsByCid[cid])
        .filter((bin) => isBulkAssemblySpacerBin(bin) && bin.residues.length > 0).length > 0
    );
  }

  private formatValidationMessagesForConstruct(
    tableConstruct: Omit<TableConstruct, 'validationMessages'>
  ): BulkAssemblyValidationMessages {
    const backendValidationResult = this.validationResults.constructResultsByCid[tableConstruct.cid] ?? null;
    const frontendValidationMessages = Object.values(tableConstruct.tableConstructFragmentByBinId).flatMap(
      (displayConstructFragment) => {
        if (displayConstructFragment.error != null) {
          return [{status: ValidationStatus.ERROR, message: displayConstructFragment.error}];
        } else {
          return [];
        }
      }
    );
    const unskippedBins = Object.values(tableConstruct.constructFragmentsByBinId).filter(
      (constructFragment) =>
        constructFragment.fragment.type !== BAValueType.RAW_TEXT ||
        constructFragment.fragment.rawText !== SKIP_BIN_SENTINEL
    );
    if (unskippedBins.length < 2) {
      frontendValidationMessages.push({
        status: ValidationStatus.ERROR,
        message: 'Constructs must contain at least two fragments',
      });
    }
    if (unskippedBins.length < this.assemblySelector.getNumFragmentBins() && this.hasNonEmptySpacers()) {
      frontendValidationMessages.push({
        status: ValidationStatus.ERROR,
        message:
          'Skipping bins in an assembly with spacers is not currently supported. Consider breaking your assembly into multiple assemblies instead.',
      });
    }
    if (
      unskippedBins.length < this.assemblySelector.getNumFragmentBins() &&
      this.assemblySelector.getSequenceTopology() === SequenceTopology.LINEAR &&
      this.assemblySelector
        .getFragmentBins()
        .some(
          (bin) =>
            this.assemblySelector.getFragmentProductionMethodForBin(baId(bin.cid)) ===
            FragmentProductionMethod.EXISTING_CUT_SITES
        )
    ) {
      frontendValidationMessages.push({
        status: ValidationStatus.ERROR,
        message:
          'Skipping bins in a linear assembly with "use existing cut sites" is not currently supported. Consider breaking your assembly into multiple assemblies instead.',
      });
    }
    if (frontendValidationMessages.length > 0) {
      return frontendValidationMessages;
    } else if (backendValidationResult == null) {
      return [];
    } else if (backendValidationResult === LOADING) {
      return LOADING;
    } else {
      return this.prepareValidationMessages(backendValidationResult.validationMessages);
    }
  }

  private getFormattedOverlapLengthForConstructFragment(
    constructFragmentValidationResult: ValidationConstructFragment,
    nextConstructFragmentValidationResult: ValidationConstructFragment | null,
    constructLength: number | null
  ): string {
    if (this.assemblySelector.getMethod().type === BulkAssemblyMethod.HOMOLOGY) {
      let overlapLengthString = '-';
      if (nextConstructFragmentValidationResult != null && constructLength != null) {
        const currentFragmentEnd =
          constructFragmentValidationResult.end !== constructLength
            ? constructFragmentValidationResult.end
            : 0;
        const nextFragmentStart =
          nextConstructFragmentValidationResult.start !== constructLength
            ? nextConstructFragmentValidationResult.start
            : 0;
        if (currentFragmentEnd !== nextFragmentStart) {
          const overlapLength = sequtil.length(currentFragmentEnd, nextFragmentStart, constructLength);
          overlapLengthString = `${overlapLength} bp`;
          assertAndContinue(
            overlapLength < constructLength,
            'Expected construct fragments to be non-overlapping '
          );
        }
      }
      return overlapLengthString;
    } else if (this.assemblySelector.getMethod().type === BulkAssemblyMethod.GIBSON) {
      if (
        constructLength == null ||
        constructFragmentValidationResult?.overlap3Start == null ||
        nextConstructFragmentValidationResult?.overlap5End == null
      ) {
        return '-';
      }
      return `${sequtil.length(
        constructFragmentValidationResult?.overlap3Start,
        nextConstructFragmentValidationResult?.overlap5End,
        constructLength
      )} bp`;
    }
    return '-';
  }

  private getFragmentDisplayName(fragment: NormalizedFragment, binCid: string): string {
    const polymer = isProteinBasedAssembly(this.assemblySelector.getMethod())
      ? fragment.protein
      : fragment.sequence;
    let fragmentName = this.getPolymerNameForBAValue(polymer) ?? '';
    if (
      this.duplicateFragmentNamesByBin[binCid] != null &&
      this.duplicateFragmentNamesByBin[binCid].has(fragmentName)
    ) {
      if (!this.canSpecifyEndpoints()) {
        fragmentName = `${fragmentName} (row ${this.getFragmentRowDescriptor(fragment)})`;
      } else {
        const {start, end} = this.getEndpointDataForDisplay(fragment);
        fragmentName = `${fragmentName} (${start.text}-${end.text})`;
      }
    }
    return fragmentName;
  }

  getConstructFragmentForDisplay(
    binCid: string,
    constructFragment: ConstructFragment,
    nextConstructFragment: ConstructFragment | undefined,
    constructFragmentValidationResults: {[fragmentId: string]: ValidationConstructFragment} | LOADING | null,
    constructLength: number | null
  ): TableConstructFragment {
    if (isBARawText(constructFragment.fragment) || !this.fragmentsByCid[constructFragment.fragment.id]) {
      const unrecognizedFragmentError = `Fragment in ${this.state.binsByCid[binCid].name} is ${
        constructFragment.fragment === BA_EMPTY_VALUE ? 'missing' : 'invalid'
      }`;
      const fragmentName = isBARawText(constructFragment.fragment) ? constructFragment.fragment.rawText : '';
      return {
        fragmentCid: null,
        fragmentName,
        fragmentEndpoints: null,
        overhangBases: '',
        overlapLength: null,
        error: fragmentName !== SKIP_BIN_SENTINEL ? unrecognizedFragmentError : undefined,
      };
    }

    const fragment = this.fragmentsByCid[constructFragment.fragment.id];
    const nextFragment =
      nextConstructFragment?.fragment?.type !== BAValueType.ID
        ? null
        : this.fragmentsByCid[nextConstructFragment.fragment.id];
    const fragmentName = this.getFragmentDisplayName(fragment, binCid);

    if (constructFragmentValidationResults == null) {
      return {
        fragmentCid: constructFragment.fragment.id,
        fragmentName,
        fragmentEndpoints: null,
        overhangBases: '',
        overlapLength: null,
      };
    } else if (constructFragmentValidationResults === LOADING) {
      return {
        fragmentCid: constructFragment.fragment.id,
        fragmentName,
        fragmentEndpoints: BULK_ASSEMBLY_LOADING_MESSAGE,
        overhangBases: BULK_ASSEMBLY_LOADING_MESSAGE,
        overlapLength: BULK_ASSEMBLY_LOADING_MESSAGE,
      };
    }

    const constructFragmentValidationResult = constructFragmentValidationResults[fragment.cid];
    const polymerData = this.getPolymerDataForBAValues(polymerDataFromFragment(fragment));

    return {
      fragmentCid: constructFragment.fragment.id,
      fragmentName,
      fragmentEndpoints:
        polymerData != null
          ? `${
              constructFragmentValidationResult?.inputStart != null
                ? modelStartToDisplay(constructFragmentValidationResult?.inputStart, polymerData.startIndex)
                : '-'
            } / ${
              constructFragmentValidationResult?.inputEnd != null
                ? modelEndToDisplay(
                    constructFragmentValidationResult?.inputEnd,
                    polymerData.residues.length,
                    polymerData.startIndex
                  )
                : '-'
            }`
          : '- / -',
      overhangBases: constructFragmentValidationResult?.overhang3Bases ?? '',
      overlapLength: this.getFormattedOverlapLengthForConstructFragment(
        constructFragmentValidationResult,
        nextFragment != null ? constructFragmentValidationResults[nextFragment.cid] : null,
        constructLength
      ),
    };
  }

  getPrimersForDisplay(validationResult: BulkAssemblyConstructValidationResult): Array<DisplayPrimer> {
    return validationResult.primerValidationResults.map((primerValidationResult): DisplayPrimer => {
      const basePrimer: BaseDisplayPrimer = {
        fragmentName:
          this.getPolymerNameForBAValue(this.fragmentsByCid[primerValidationResult.fragmentId].sequence) ??
          '',
        orientation: primerValidationResult.sequencePrime,
        name: primerValidationResult.primer?.oligoContents.name ?? null,
        bases: primerValidationResult.primer?.oligoContents.bases ?? null,
        wholeMeltingTemperature: primerValidationResult.primer?.wholeTm ?? null,
        validationMessages: this.prepareValidationMessages(primerValidationResult.validationMessages),
      };

      if (primerValidationResult.primer?.oligoSource === BulkAssemblyOligoSource.PREFERRED_PRIMER) {
        return {
          primerType: PrimerType.USE_EXISTING,
          oligoId: primerValidationResult.primer.oligoId ?? null,
          ...basePrimer,
        };
      } else {
        assert(
          primerValidationResult.primer?.oligoSource === BulkAssemblyOligoSource.DESIGNED,
          'Unexpected oligo source'
        );
        return {
          primerType: PrimerType.CREATE_NEW,
          createdOligoId: primerValidationResult.primer?.oligoId ?? null,
          ...basePrimer,
        };
      }
    });
  }

  /**
   * Check if a fragment's bin, start, end, and orientation are filled in and valid.
   */
  isValidFragmentForAutopopulate(fragmentCid: string): boolean {
    const fragment = this.fragmentsByCid[fragmentCid];
    const fragmentValidation = this.validationResults.fragmentResultsByCid[fragmentCid];
    const {start, end} = this.getEndpointDataForDisplay(fragment);
    if (
      fragmentValidation == null ||
      fragmentValidation === LOADING ||
      fragmentValidation.validationMessages.some((message) => message.status === ValidationStatus.ERROR) ||
      this.getPolymerDataForBAValues(polymerDataFromFragment(fragment)) == null ||
      !isBAId(fragment.bin) ||
      start.error != null ||
      end.error != null ||
      !isBAOrientation(fragment.orientation)
    ) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Check if anything should block the auto-populate button from being enabled. Return a specific error
   * if it should not be enabled.
   *
   * The button should only be enabled if all of the following are true:
   * - All rows in the fragments table are valid and complete - that is, each fragment's bin, start, end, and
   *   orientation are filled in, and each fragment is associated with a sequence, and backend fragment
   *   validation has completed successfully
   * - All bins have at least one fragment
   * - Completing the autopopulate would not result in generating more constructs than allowed by the product
   *   limits
   * - There are not already constructs in the assembly
   */
  getAutopopulateButtonStatus(): string | null {
    const layoutErrorMessages = this.getLayoutErrorMessages();
    const areAllFragmentsValid = this.state.fragments.every((fragment) =>
      this.isValidFragmentForAutopopulate(fragment.cid)
    );
    const numBinsWithSpecifiedFragments = new Set(
      this.state.fragments.flatMap((fragment) => (isBAId(fragment.bin) ? [fragment.bin.id] : []))
    ).size;
    const numConstructsToBeAutopopulated = this.getOrderedFragmentListsForAutopopulate().reduce(
      (product, fragmentList) => product * fragmentList.length,
      1
    );

    if (layoutErrorMessages.length > 0) {
      return layoutErrorMessages[0];
    } else if (!areAllFragmentsValid) {
      return "You can't autopopulate constructs because there are incomplete and/or invalid rows in the fragments table.";
    } else if (numBinsWithSpecifiedFragments !== this.assemblySelector.getNumFragmentBins()) {
      return "You can't autopopulate constructs until every bin has a valid fragment.";
    } else if (numConstructsToBeAutopopulated > getMaxConstructCount()) {
      return `You can't autopopulate constructs because the number of possible combinations exceeds Benchling's maximum permissible limit of ${getMaxConstructCount()} constructs.`;
    } else if (this.getNumConstructs() !== 0) {
      return "You can't autopopulate constructs since the table is already partially filled. Please remove all existing rows to enable this option.";
    } else {
      return null;
    }
  }

  /**
   * Add a set of new fragments to the store.
   *
   * @param fragments List of fragments to add.
   */
  addFragments(fragments: Array<NormalizedFragment>): void {
    this.resolveAndUpdateState({
      ...this.state,
      fragments: [...this.state.fragments, ...fragments],
    });
  }

  updateMethodParametersAndTopology(
    methodParameters: MethodParameters,
    topology: BulkAssemblyTopology
  ): void {
    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        draft.method.parameters = methodParameters;
        draft.topology = topology;
      })
    );
  }

  updateFragments(updatedFragments: Array<NormalizedFragment>): void {
    const normalizedFragmentByCid = groupByUniqueKey(
      updatedFragments,
      (updatedFragment) => updatedFragment.cid
    );
    this.resolveAndUpdateState({
      ...this.state,
      fragments: this.state.fragments.map((fragment) => normalizedFragmentByCid[fragment.cid] ?? fragment),
    });
  }

  private getFragmentsByCids(fragmentCids: Set<string>): Array<NormalizedFragment> {
    const resultFragments = [];
    for (const fragmentCid of fragmentCids) {
      const fragment = this.fragmentsByCid[fragmentCid];
      if (fragment != null) {
        resultFragments.push(fragment);
      }
    }
    return resultFragments;
  }

  replaceFragments(newFragments: Array<NormalizedFragment>, filteredOutFragmentCids: Set<string>): void {
    this.resolveAndUpdateState({
      ...this.state,
      fragments: this.getFragmentsByCids(filteredOutFragmentCids).concat(newFragments),
    });
  }

  getFilteredFragmentCidsByStatus(): {[status in ValidationStatus]: Set<string>} {
    const tableFragments = this.state.fragments.map((normalizedFragment) =>
      this.getTableFragment(normalizedFragment)
    );
    return {
      [ValidationStatus.SUCCESS]: new Set(
        this.filterByValidationStatus(tableFragments, ValidationStatus.SUCCESS).map(({cid}) => cid)
      ),
      [ValidationStatus.WARNING]: new Set(
        this.filterByValidationStatus(tableFragments, ValidationStatus.WARNING).map(({cid}) => cid)
      ),
      [ValidationStatus.ERROR]: new Set(
        this.filterByValidationStatus(tableFragments, ValidationStatus.ERROR).map(({cid}) => cid)
      ),
    };
  }

  deleteFilteredFragments(): void {
    const fragmentStatusFilter = this.getFragmentStatusFilter();
    if (fragmentStatusFilter == null) {
      assertAndContinue(false, 'Cannot delete filtered fragments when no filter applied');
      return;
    }
    const filteredFragmentCids = this.getFilteredFragmentCidsByStatus()[fragmentStatusFilter];
    this.replaceFragments(
      this.state.fragments.filter(({cid}) => !filteredFragmentCids.has(cid)),
      new Set()
    );
  }

  handleTableFragmentsUpdate(fragments: Array<FragmentUpdate>, filteredOutFragmentCids: Set<string>): void {
    const binResolver = getNameResolver(this.assemblySelector.getFragmentBins(), (bin) => bin.name);
    this.resolveAndUpdateState({
      ...this.state,
      fragments: this.getFragmentsByCids(filteredOutFragmentCids).concat(
        fragments.map((fragment) => this.normalizeFragmentUpdateFromTable(fragment, binResolver))
      ),
    });
  }

  private normalizeFragmentUpdateFromTable(
    fragmentUpdate: FragmentUpdate,
    binResolver: NameResolver<BulkAssemblyFragmentBin>
  ): NormalizedFragment {
    const parsedBin = this.parseBin(fragmentUpdate.bin, binResolver);
    const canHavePrimers = this.enableSelectPreferredPrimers(parsedBin);
    const oldFragment = fragmentUpdate.cid != null ? this.fragmentsByCid[fragmentUpdate.cid] : null;
    const oldFragmenttData = oldFragment != null ? polymerDataFromFragment(oldFragment) : null;
    const polymerData = this.getPolymerDataForBAValues(fragmentUpdate);

    // Need to decide whether to interpret the update as a protein or sequence.
    let basicNormalizedData;
    if (this.supportedPolymerSubtypes()[0] === PolymerSubtype.aa_sequence) {
      basicNormalizedData = {
        cid: fragmentUpdate.cid ?? uuidv4(),
        bin: parsedBin,
        preferredPrimer3: canHavePrimers ? fragmentUpdate.preferredPrimer3 : BA_EMPTY_VALUE,
        preferredPrimer5: canHavePrimers ? fragmentUpdate.preferredPrimer5 : BA_EMPTY_VALUE,
        restrictionEnzyme5: BA_EMPTY_VALUE,
        restrictionEnzyme3: BA_EMPTY_VALUE,
        protein: fragmentUpdate.polymer,
        proteinVersionDbId: fragmentUpdate.polymerVersionDbId,
        sequence: BA_EMPTY_VALUE,
        sequenceVersionDbId: null,
      };
    } else {
      basicNormalizedData = {
        cid: fragmentUpdate.cid ?? uuidv4(),
        bin: parsedBin,
        // TODO(BNCH-57189): Show a warning to the user before removing existing preferred primers
        preferredPrimer3: canHavePrimers ? fragmentUpdate.preferredPrimer3 : BA_EMPTY_VALUE,
        preferredPrimer5: canHavePrimers ? fragmentUpdate.preferredPrimer5 : BA_EMPTY_VALUE,
        restrictionEnzyme5: fragmentUpdate.restrictionEnzyme5,
        restrictionEnzyme3: fragmentUpdate.restrictionEnzyme3,
        sequence: fragmentUpdate.polymer,
        sequenceVersionDbId: fragmentUpdate.polymerVersionDbId,
        protein: BA_EMPTY_VALUE,
        proteinVersionDbId: null,
      };
    }

    if (
      polymerData != null &&
      isBAId(fragmentUpdate.polymer) &&
      // If we previously had a sequence that was different from the current sequence, don't handle
      // start and end because they might have been carried over from the old sequence position.
      (oldFragmenttData == null ||
        oldFragmenttData.polymer.type === BAValueType.RAW_TEXT ||
        oldFragmenttData.polymer.id === fragmentUpdate.polymer.id)
    ) {
      // Parse start and end with respect to the sequence contents.
      const implicitOrientation = getOrientationWithFallback(fragmentUpdate.orientation);
      // If the interpreted orientation has changed, then we need to swap the way we interpret the endpoints.
      let shouldParseEndpointsByOppositeOrientation = false;
      if (
        oldFragment != null &&
        isBARawText(fragmentUpdate.start) &&
        isBARawText(fragmentUpdate.end) &&
        implicitOrientation !== getOrientationWithFallback(oldFragment.orientation)
      ) {
        const {start: previousStart, end: previousEnd} = this.getEndpointDataForDisplay(oldFragment);
        shouldParseEndpointsByOppositeOrientation =
          fragmentUpdate.start.rawText === previousStart.text &&
          fragmentUpdate.end.rawText === previousEnd.text;
      }
      const displayToModelConverters = getDisplayToModelConverters(polymerData.startIndex);
      const inputEndpoints = getValueForOrientation(
        // Swap parsing if necessary.
        shouldParseEndpointsByOppositeOrientation
          ? OPPOSITE_BA_ORIENTATION[implicitOrientation]
          : implicitOrientation,
        {
          start: parseBAEndpoint(fragmentUpdate.start, displayToModelConverters.start),
          end: parseBAEndpoint(fragmentUpdate.end, displayToModelConverters.end),
        },
        {
          start: parseBAEndpoint(fragmentUpdate.end, displayToModelConverters.start),
          end: parseBAEndpoint(fragmentUpdate.start, displayToModelConverters.end),
        }
      );
      const {orientation: orientationOverride, ...endpoints} = handleEndpointChange(
        inputEndpoints,
        implicitOrientation,
        polymerData.circular,
        polymerData.singleStranded
      );
      return {
        ...basicNormalizedData,
        ...endpoints,
        orientation:
          orientationOverride != null
            ? baOrientation(orientationOverride)
            : parseBAOrientation(fragmentUpdate.orientation),
      };
    }
    // This clobbers any endpoint and version values when the user changes the sequence.
    return {
      ...basicNormalizedData,
      sequenceVersionDbId: null,
      proteinVersionDbId: null,
      start: BA_EMPTY_VALUE,
      end: BA_EMPTY_VALUE,
      orientation: baOrientation(BulkAssemblyOrientation.FORWARD),
    };
  }

  /**
   * Resolve a "bin ID or raw text" value to a bin ID if possible.
   */
  parseBin(
    bin: BAIdValue | BARawTextValue,
    binResolver: NameResolver<BulkAssemblyFragmentBin>
  ): BAIdValue | BARawTextValue {
    if (bin.type === BAValueType.ID) {
      return bin;
    }
    const resolution = binResolver(bin.rawText);
    if (resolution.type === NameResolutionType.FOUND) {
      return baId(resolution.value.cid);
    } else {
      return bin;
    }
  }

  getFilteredConstructCidsByStatus(): {[status in ValidationStatus]: Set<string>} {
    const tableConstructs = this.state.constructs.map((normalizedConstruct) =>
      this.getTableConstruct(normalizedConstruct)
    );
    return {
      [ValidationStatus.SUCCESS]: new Set(
        this.filterByValidationStatus(tableConstructs, ValidationStatus.SUCCESS).map(({cid}) => cid)
      ),
      [ValidationStatus.WARNING]: new Set(
        this.filterByValidationStatus(tableConstructs, ValidationStatus.WARNING).map(({cid}) => cid)
      ),
      [ValidationStatus.ERROR]: new Set(
        this.filterByValidationStatus(tableConstructs, ValidationStatus.ERROR).map(({cid}) => cid)
      ),
    };
  }

  deleteFilteredConstructs(): void {
    const constructStatusFilter = this.constructStatusFilter;
    if (constructStatusFilter == null) {
      assertAndContinue(false, 'Cannot delete filtered constructs when no filter applied');
      return;
    }
    const filteredConstructCids = this.getFilteredConstructCidsByStatus()[constructStatusFilter];
    this.replaceConstructs(this.state.constructs.filter(({cid}) => !filteredConstructCids.has(cid)));
  }

  replaceConstructs(newConstructs: Array<NormalizedConstruct>): void {
    this.resolveAndUpdateState({
      ...this.state,
      constructs: newConstructs,
    });
  }

  handleTableConstructsUpdate(
    constructs: Array<ConstructUpdate>,
    filteredOutConstructCids?: Set<string>
  ): void {
    const fragmentResolverByBinCid = mapObject(this.specifiedFragmentInfosByBinCid, (fragmentInfos) =>
      getNameResolver(
        // Don't allow resolution for fragments with an empty sequence name, since the sequence is
        // not loaded yet.
        fragmentInfos.filter((fragmentInfo) => fragmentInfo.polymerName),
        (fragmentInfo) => fragmentInfo.polymerName
      )
    );
    const filteredConstructs: Array<NormalizedConstruct> = [];
    if (filteredOutConstructCids != null) {
      const constructsByCid = this.getConstructsByCid();
      for (const constructCid of filteredOutConstructCids) {
        const construct = constructsByCid[constructCid];
        if (construct != null) {
          filteredConstructs.push(construct);
        }
      }
    }

    this.resolveAndUpdateState(
      produce(this.state, (draft) => {
        const constructUpdates = constructs.map(
          (constructUpdate): NormalizedConstruct => ({
            cid: constructUpdate.cid ?? uuidv4(),
            constructFragmentsByBinId: objectFromEntries(
              Object.entries(constructUpdate.constructFragmentsByBinId).map(([binCid, constructFragment]) => {
                let resolvedFragment = constructFragment.fragment;
                const nameResolver = fragmentResolverByBinCid[binCid];
                if (isBARawText(constructFragment.fragment) && nameResolver != null) {
                  const resolution = nameResolver(constructFragment.fragment.rawText);
                  if (resolution.type === NameResolutionType.FOUND) {
                    resolvedFragment = baId(resolution.value.fragmentCid);
                  }
                }
                return [binCid, {fragment: resolvedFragment}];
              })
            ),
          })
        );
        draft.constructs = filteredConstructs.concat(constructUpdates);
      })
    );
  }

  getNumConstructs(): number {
    return this.state.constructs.length;
  }

  getFilledOutFragments(): Array<NormalizedFragment> {
    return this.state.fragments.filter(
      (fragment) => this.validationResults.fragmentResultsByCid[fragment.cid] != null
    );
  }

  getNumFilledOutConstructs(): number {
    return Object.values(this.validationResults.constructResultsByCid).filter(
      (constructValidationResult) => constructValidationResult != null
    ).length;
  }

  getNumValidConstructs(): number {
    return Object.values(this.validationResults.constructResultsByCid).filter(
      (constructValidationResult) =>
        constructValidationResult != null &&
        constructValidationResult !== LOADING &&
        Object.values(constructValidationResult.validationMessages).every(
          (message) => message.status !== ValidationStatus.ERROR
        )
    ).length;
  }

  areAllConstructsSpecified(): boolean {
    return this.state.constructs.every(
      (construct) => this.validationResults.constructResultsByCid[construct.cid] != null
    );
  }

  areAllConstructsValid(): boolean {
    return this.getNumValidConstructs() === this.getNumConstructs();
  }

  getNumDesignedOligos(prime?: SequencePrime): number {
    const designedOligoBases = Object.values(this.validationResults.constructResultsByCid).flatMap(
      (validationResult) => {
        if (validationResult != null && validationResult !== LOADING) {
          const boundPrimers = validationResult.primerValidationResults.filter(
            (primerValidationResult) =>
              primerValidationResult.primer != null &&
              primerValidationResult.primer.oligoSource === BulkAssemblyOligoSource.DESIGNED &&
              (prime == null || primerValidationResult.sequencePrime === prime)
          );
          return boundPrimers.map((boundPrimer) => boundPrimer.primer!.oligoContents.bases.toUpperCase());
        } else {
          return [];
        }
      }
    );
    // The backend de-duplicates designed oligos by their bases, so just count the number of unique bases.
    return new Set(designedOligoBases).size;
  }

  getPreferredPrimerIds(prime: SequencePrime): Array<string> {
    const preferredPrimerIds = Object.values(this.validationResults.constructResultsByCid).flatMap(
      (validationResult) => {
        if (validationResult != null && validationResult !== LOADING) {
          const boundPrimers = validationResult.primerValidationResults.filter(
            (primerValidationResult) =>
              primerValidationResult.primer != null &&
              primerValidationResult.primer.oligoSource === BulkAssemblyOligoSource.PREFERRED_PRIMER &&
              primerValidationResult.sequencePrime === prime
          );
          return boundPrimers.map((boundPrimer) => boundPrimer.primer!.oligoId!);
        } else {
          return [];
        }
      }
    );
    return Array.from(new Set(preferredPrimerIds));
  }

  /**
   * Gets a Set of layout validation errors present in the whole assembly.
   */
  getLayoutHasErrorTypes(): Set<BulkAssemblyLayoutError> {
    const {errorsByBin, otherErrors} = this.assemblyLayoutValidator.getAssemblyLayoutValidationErrors();
    return Object.values(errorsByBin).reduce<Set<BulkAssemblyLayoutError>>(
      (acc: Set<BulkAssemblyLayoutError>, binErrors: Set<BulkAssemblyLayoutError>) => {
        binErrors.forEach((binError) => acc.add(binError));
        return acc;
      },
      new Set<BulkAssemblyLayoutError>(otherErrors)
    );
  }

  /**
   * Gets a list of layout validation error messages present in the whole assembly.
   *
   * We manually order the errors rather than using the iterator provided by Set so that we can have an
   * intrinsically deterministic order of error texts.
   */
  getLayoutErrorMessages(): Array<string> {
    const errorsByType = this.getLayoutHasErrorTypes();
    const errors = sortedBy([...errorsByType], [(error) => error]);
    return errors.map((error) => getErrorText(error, this.assemblySelector.getMethod().type));
  }

  /**
   * Gets a user-facing error message for display on the Assemble button. A return value of null indicates
   * no assembly-blocking errors.
   */
  getAssemblyFinalizationErrorMessage(): string | null {
    const layoutErrorMessages = this.getLayoutErrorMessages();

    if (layoutErrorMessages.length > 0) {
      return layoutErrorMessages[0];
    } else if (this.getNumConstructs() === 0) {
      return 'Specify valid constructs to finalize your assembly.';
    } else if (!this.areAllConstructsSpecified()) {
      return 'Add missing fragments to constructs, or choose to skip them, to finalize your assembly.';
    } else if (!this.areAllConstructsValid()) {
      return 'Resolve construct errors to finalize your assembly.';
    } else {
      return null;
    }
  }

  supportedPolymerSubtypes(): Array<PolymerSubtype> {
    const methodData = this.assemblySelector.getMethod();
    if (methodData.type === BulkAssemblyMethod.CONCATENATION) {
      if (methodData.parameters.polymerSubtype === PolymerSubtype.dna_sequence) {
        return [methodData.parameters.polymerSubtype, PolymerSubtype.dna_oligo];
      } else if (methodData.parameters.polymerSubtype === PolymerSubtype.rna_sequence) {
        return [methodData.parameters.polymerSubtype, PolymerSubtype.rna_oligo];
      } else {
        return [methodData.parameters.polymerSubtype];
      }
    } else {
      return [PolymerSubtype.dna_sequence];
    }
  }

  getFragmentIcon(): BenchlingIconName {
    const sequenceSubtypes = this.supportedPolymerSubtypes();
    if (sequenceSubtypes.includes(PolymerSubtype.dna_sequence)) {
      return FolderItemIcon.Sequence;
    } else if (sequenceSubtypes.includes(PolymerSubtype.rna_sequence)) {
      return FolderItemIcon.RNASequence;
    } else if (sequenceSubtypes.includes(PolymerSubtype.aa_sequence)) {
      return FolderItemIcon.Protein;
    } else {
      return FolderItemIcon.OligoV2;
    }
  }

  getFragmentStatusFilter(): ValidationStatus | null {
    return this.fragmentStatusFilter;
  }

  setFragmentStatusFilter(statusFilter: ValidationStatus | null): void {
    getStore().dispatch({
      type: BulkAssemblyActionType.BULK_ASSEMBLY_FRAGMENT_STATUS_FILTER_UPDATED,
      assemblyId: this.assemblyId,
      statusFilter,
    });
  }

  getConstructStatusFilter(): ValidationStatus | null {
    return this.constructStatusFilter;
  }

  setConstructStatusFilter(statusFilter: ValidationStatus | null): void {
    getStore().dispatch({
      type: BulkAssemblyActionType.BULK_ASSEMBLY_CONSTRUCT_STATUS_FILTER_UPDATED,
      assemblyId: this.assemblyId,
      statusFilter,
    });
  }
}
